import React, { useState, useEffect } from 'react';
import './App.css';
import { db } from './firebase';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, orderBy, getDocs, getDoc, enableIndexedDbPersistence, writeBatch, arrayUnion } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import LoginScreen from './LoginScreen';
import ProjectsList from './ProjectsList';
import ProjectDetail from './ProjectDetail';
import AreaForm from './AreaForm';
import AssessForm from './AssessForm';
import ReviewMode from './ReviewMode';
import HelpOverlay from './HelpOverlay';
import ErrorBoundary from './ErrorBoundary';
import { hydrateArea, hydrateProject, WORKFLOW } from './dataModel';
import { entryM2, isUserMeasured } from './calcUtils';
import { GIRTH_DISPLAY } from './areaConstants';
import { setAuthDisplayName } from './sessionLock';
import { setUpdateCallback, applyUpdate } from './swRegistration';
import ToastContainer from './Toast';

// Enable offline persistence (silent fail if already enabled)
try { enableIndexedDbPersistence(db).catch(() => {}); } catch {}

export const IS_DESKTOP = () => window.innerWidth >= 1024;

function stripUndefined(obj) {
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v === undefined ? null : stripUndefined(v)])
    );
  }
  return obj;
}

function App() {
  const { user, profile, loading: authLoading, signOut } = useAuth();

  // Keep session lock name in sync with the authenticated user
  useEffect(() => {
    setAuthDisplayName(user?.displayName || profile?.displayName || null);
  }, [user, profile]);

  if (authLoading) return (
    <div className="s2s-loading"><div className="spinner spinner-lg" aria-label="Loading"></div><p>Loading…</p></div>
  );
  if (!user || !profile?.approved) return <LoginScreen />;

  // Normalize roles — handle both field names (role/roles) and formats (string/array)
  const rawRoles = profile.roles || profile.role || [];
  const normalizedRoles = Array.isArray(rawRoles) ? rawRoles : (typeof rawRoles === 'string' && rawRoles ? [rawRoles] : []);
  const primaryRole = normalizedRoles[0] || 'measurer';

  const currentUser = {
    uid:         user.uid,
    email:       user.email,
    displayName: profile.displayName || user.displayName || user.email,
    roles:       normalizedRoles,
    role:        primaryRole,
  };
  return <MainApp userId={user.uid} currentUser={currentUser} signOut={signOut} />;
}

// ── Sync denormalized counts from areas subcollection back to project doc ────
async function syncProjectCounts(projectId) {
  const areasSnap = await getDocs(collection(db, 'projects', projectId, 'areas'));
  const allAreas = areasSnap.docs.map(d => d.data());
  let heroUrl = null;
  let heroThumbnailUrl = null;
  for (const a of allAreas) {
    const hero = (a.photos || []).find(p => p.isProjectHero);
    if (hero) { heroUrl = hero.url; heroThumbnailUrl = hero.thumbnailUrl || hero.url; break; }
  }
  // Count areas where every measured substrate has assessed hours
  const assessedAreaCount = allAreas.filter(a => {
    const ad = a.assessBySubstrate || {};
    const subs = [...new Set((a.surfaceEntries || []).map(e => e.substrate).filter(Boolean))];
    return subs.length > 0 && subs.every(s => parseFloat(ad[s]?.hours) > 0);
  }).length;
  // Count areas where surfaces exist AND every entry has real dimensions
  const areasWithSurfaces = allAreas.filter(a => (a.surfaceEntries || []).length > 0);
  const measuredAreaCount = areasWithSurfaces.filter(a => {
    return (a.surfaceEntries || []).every(e => entryM2(e.substrate, e) > 0);
  }).length;
  await updateDoc(doc(db, 'projects', projectId), {
    areaCount: allAreas.length,
    internalCount: allAreas.filter(a => a.type === 'internal').length,
    externalCount: allAreas.filter(a => a.type === 'external').length,
    photoCount: allAreas.reduce((s, a) => s + (a.photos || []).length, 0),
    finishedCount: allAreas.filter(a => a.status === 'finished').length,
    areasWithSurfacesCount: areasWithSurfaces.length,
    measuredAreaCount,
    assessedAreaCount,
    heroPhotoUrl: heroUrl,
    heroThumbnailUrl,
    updatedAt: new Date().toISOString(),
  });
}

function MainApp({ userId, currentUser, signOut }) {
  const [swUpdateWaiting, setSwUpdateWaiting] = useState(false);

  useEffect(() => {
    setUpdateCallback((status) => {
      if (status === 'waiting') setSwUpdateWaiting(true);
    });
    return () => setUpdateCallback(null);
  }, []);

  const [projects, setProjects]         = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [areas, setAreas]               = useState([]);
  const [currentArea, setCurrentArea]   = useState(null);
  const [areaFormType, setAreaFormType] = useState(null);
  const [areaFormKey, setAreaFormKey] = useState(0);
  const [loading, setLoading]           = useState(true);
  const [screen, setScreen]             = useState('projects');
  const [showArchived, setShowArchived] = useState(false);
  const [successPrompt, setSuccessPrompt] = useState(null);
  const [appToast, setAppToast] = useState(null); // { message } — non-blocking, auto-dismisses
  const appToastTimer = React.useRef(null);
  const showAppToast = (message) => {
    clearTimeout(appToastTimer.current);
    setAppToast(message);
    appToastTimer.current = setTimeout(() => setAppToast(null), 4000);
  };
  const [assessTargetAreaId, setAssessTargetAreaId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // Global Escape key handler — closes topmost overlay
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (showHelp) { setShowHelp(false); return; }
      if (successPrompt) { setSuccessPrompt(null); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showHelp, successPrompt]);
  const [activeRole, setActiveRoleRaw] = useState(() => {
    const profileDefault = currentUser?.role || 'measurer';
    try {
      const stored = localStorage.getItem('s2s_activeRole');
      if (!stored) return profileDefault;
      const allowed = (currentUser?.roles || []).length > 0
        ? currentUser.roles
        : [profileDefault];
      return allowed.includes(stored) ? stored : profileDefault;
    } catch { return profileDefault; }
  });
  const setActiveRole = (role) => {
    setActiveRoleRaw(role);
    try { localStorage.setItem('s2s_activeRole', role); } catch {};
  };
  // Revalidate activeRole when Firestore profile changes (live onSnapshot updates)
  const currentRoleRef = React.useRef(currentUser?.role);
  useEffect(() => {
    if (currentUser?.role !== currentRoleRef.current) {
      currentRoleRef.current = currentUser?.role;
      const allowed = (currentUser?.roles || []).length > 0
        ? currentUser.roles
        : [currentUser?.role || 'measurer'];
      if (!allowed.includes(activeRole)) {
        setActiveRole(currentUser?.role || 'measurer');
      }
    }
  }, [currentUser?.role, currentUser?.roles?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Activity log ────────────────────────────────────────────────────────
  const logActivity = async (pid, action, detail) => {
    if (!pid) return;
    try {
      await addDoc(collection(db, 'projects', pid, 'activity'), {
        action,
        detail: detail || '',
        who: currentUser?.displayName || currentUser?.email || 'Unknown',
        uid: userId,
        at: new Date().toISOString(),
      });
    } catch (e) { console.warn('Activity log failed:', e); }
  };

  // ── Projects listener (metadata only — no areas embedded) ──────────────
  const syncedRef = React.useRef(new Set());
  useEffect(() => {
    const q = query(
      collection(db, 'projects'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => hydrateProject({ firestoreId: d.id, ...d.data() }));
      setProjects(list);
      setLoading(false);
      // Backfill: sync counts for projects missing the new fields (runs once per project)
      list.forEach(p => {
        if (p.firestoreId && p.measuredAreaCount === undefined && !syncedRef.current.has(p.firestoreId)) {
          syncedRef.current.add(p.firestoreId);
          syncProjectCounts(p.firestoreId).catch(() => {});
        }
      });
    }, () => setLoading(false));
    return () => unsub();
  }, [userId]);

  // ── Areas listener (subcollection — active only when viewing a project) ─
  useEffect(() => {
    if (!currentProject?.firestoreId || screen === 'projects') { setAreas([]); return; }
    const q = query(
      collection(db, 'projects', currentProject.firestoreId, 'areas'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setAreas(snap.docs.map(d => {
        const { firestoreId: _ignore, ...hydrated } = hydrateArea(d.data());
        return { firestoreId: d.id, ...hydrated };
      }));
    });
    return () => unsub();
  }, [currentProject?.firestoreId, screen]);

  // Build a "project with areas" object for components that need it
  const projectWithAreas = currentProject ? { ...currentProject, areas } : null;

  // Areas in the order the project hub lists them — internal group first, then
  // external. Used to walk from one area to the next without bouncing back to
  // the hub (AreaForm's "Save → Next Area").
  const orderedAreas = [
    ...areas.filter(a => a.type === 'internal'),
    ...areas.filter(a => a.type !== 'internal'),
  ];
  const currentAreaIndex = currentArea?.firestoreId
    ? orderedAreas.findIndex(a => a.firestoreId === currentArea.firestoreId)
    : -1;
  const nextAreaInSequence = currentAreaIndex >= 0 ? (orderedAreas[currentAreaIndex + 1] || null) : null;

  // Open an existing area in the measurement form (used by Save → Next Area)
  const goToArea = (area) => {
    setAreaFormType(area.type);
    setCurrentArea(area);
    setAreaFormKey(k => k + 1);
    setScreen('area');
  };

  // ── Project CRUD ───────────────────────────────────────────────────────
  const createProject = async (data) => {
    const ref = await addDoc(collection(db, 'projects'), {
      ...data, userId, createdByUid: userId,
      projectCreator: data.projectCreator || currentUser?.displayName || '',
      createdByName: currentUser?.displayName || '',
      contributors: [userId], measuredByNames: [], assessedByNames: [], status: 'active', workflow: WORKFLOW.MEASURING,
      areaCount: 0, internalCount: 0, externalCount: 0, photoCount: 0, finishedCount: 0, heroPhotoUrl: null,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    });
    setCurrentProject(hydrateProject({ firestoreId: ref.id, ...data, status: 'active' }));
    setScreen('project');
    logActivity(ref.id, 'created', `Project created`);
  };

  const saveProjectDetails = async (updated) => {
    const { firestoreId, areas: _a, ...data } = updated;
    await updateDoc(doc(db, 'projects', firestoreId), { ...stripUndefined(data), updatedAt: new Date().toISOString() });
    setCurrentProject(prev => prev?.firestoreId === firestoreId ? { ...prev, ...data } : prev);
  };

  const archiveProject = async (firestoreId) => {
    await updateDoc(doc(db, 'projects', firestoreId), { status: 'archived', updatedAt: new Date().toISOString() });
    if (currentProject?.firestoreId === firestoreId) { setCurrentProject(null); setScreen('projects'); }
  };

  const unarchiveProject = async (firestoreId) => {
    await updateDoc(doc(db, 'projects', firestoreId), { status: 'active', updatedAt: new Date().toISOString() });
  };

  // ── Workflow transitions ────────────────────────────────────────────────
  const setProjectWorkflow = async (firestoreId, workflow, extra = {}) => {
    await updateDoc(doc(db, 'projects', firestoreId), {
      workflow, ...extra, updatedAt: new Date().toISOString(),
    });
    setCurrentProject(prev => prev?.firestoreId === firestoreId ? { ...prev, workflow, ...extra } : prev);
  };

  const deleteProject = async (firestoreId) => {
    // Delete all area subcollection docs first
    const areasSnap = await getDocs(collection(db, 'projects', firestoreId, 'areas'));
    for (const areaDoc of areasSnap.docs) { await deleteDoc(areaDoc.ref); }
    await deleteDoc(doc(db, 'projects', firestoreId));
    if (currentProject?.firestoreId === firestoreId) { setCurrentProject(null); setScreen('projects'); }
  };

  const updateProject = async (firestoreId, fields) => {
    await updateDoc(doc(db, 'projects', firestoreId), fields);
  };

  // ── Area CRUD (subcollection) ──────────────────────────────────────────
  const openNewArea  = (type, previousName) => {
    const prefix = type === 'internal' ? 'Internal' : 'External';
    const pattern = new RegExp(`^${prefix}\\s+(\\d+)$`);
    const areas = currentProject?.areas || [];
    // Scan all existing area names for the highest number used
    let maxNum = 0;
    for (const a of areas) {
      const m = (a.name || '').match(pattern);
      if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
    }
    // Also check the just-saved area name (may not be in snapshot yet after background save)
    if (previousName) {
      const cm = previousName.match(pattern);
      if (cm) maxNum = Math.max(maxNum, parseInt(cm[1], 10));
    }
    const defaultName = `${prefix} ${maxNum + 1}`;
    setAreaFormType(type);
    setCurrentArea({ isNew: true, name: defaultName });
    setAreaFormKey(k => k + 1);
    setScreen('area');
  };

  const openEditArea = async (area) => {
    if (!area.firestoreId || !currentProject?.firestoreId) {
      setAreaFormType(area.type); setCurrentArea(area); setAreaFormKey(k => k + 1); setScreen('area');
      return;
    }
    try {
      const areaRef = doc(db, 'projects', currentProject.firestoreId, 'areas', area.firestoreId);
      const snap = await getDoc(areaRef);
      if (!snap.exists()) {
        setSuccessPrompt({ title: '⚠️ Area not found', message: 'This area could not be found in the database. It may have been deleted. Please refresh the page.' });
        return;
      }
      setAreaFormType(area.type); setCurrentArea(area); setAreaFormKey(k => k + 1); setScreen('area');
    } catch (err) {
      console.error('openEditArea failed:', err);
      setSuccessPrompt({ title: '⚠️ Could not open area', message: `Something went wrong opening this area.\n\n${err.message}\n\nPlease try again or refresh the page.` });
    }
  };

  // If this area has a hero photo, clear isProjectHero from all other areas in the project
  const clearHeroFromOtherAreas = async (pid, thisAreaId) => {
    const otherAreas = areas.filter(a => a.firestoreId && a.firestoreId !== thisAreaId);
    const affected = otherAreas.filter(a => (a.photos || []).some(p => p.isProjectHero));
    if (affected.length === 0) return;
    const batch = writeBatch(db);
    for (const a of affected) {
      const areaRef = doc(db, 'projects', pid, 'areas', a.firestoreId);
      batch.update(areaRef, {
        photos: (a.photos || []).map(p => ({ ...p, isProjectHero: false })),
        updatedAt: new Date().toISOString(),
      });
    }
    await batch.commit();
  };

  // Strip girth from surfaceSettings for surfaces where girth is hidden (always 1.0).
  // This ensures no stale girth values persist in Firestore — the UI hides the control,
  // and this strip ensures the data matches. Calculations then safely fall through to
  // DEFAULT_GIRTHS or 1.0 with no defensive guards needed at read time.
  const stripHiddenGirth = (data) => {
    const settings = data.surfaceSettings;
    if (!settings) return data;
    const cleaned = { ...settings };
    let changed = false;
    Object.keys(cleaned).forEach(surfKey => {
      const base = cleaned[surfKey]?.baseSurface || surfKey;
      const gd = GIRTH_DISPLAY[base];
      if (gd && gd.visible === false && cleaned[surfKey]?.girth != null) {
        cleaned[surfKey] = { ...cleaned[surfKey] };
        delete cleaned[surfKey].girth;
        changed = true;
      }
    });
    return changed ? { ...data, surfaceSettings: cleaned } : data;
  };

  const saveArea = async (areaData, mode) => {
    const pid = currentProject.firestoreId;
    // Resolve the follow-on area up front — the areas snapshot can change while
    // the write is in flight.
    const advanceTo = mode === 'next_area' ? nextAreaInSequence : null;
    const areasCol = collection(db, 'projects', pid, 'areas');
    const now = new Date().toISOString();
    // Strip firestoreId — it must never be stored inside the document data
    const { firestoreId: _fid, ...rawData } = areaData;
    // Strip girth values for surfaces that should never have them
    const cleanData = stripHiddenGirth(rawData);

    // Strip assessor-owned fields — they are written ONLY by saveAssessArea.
    // This prevents a measurer's save from overwriting assessment data.
    const ASSESSOR_FIELDS = new Set([
      'assessBySubstrate', 'assessNotes', 'pmAddedSurfaces',
      'washdownHours', 'approvedHours', 'approvedBySubstrate', 'assessedBy', 'assessedAt',
    ]);
    const measurerData = Object.fromEntries(
      Object.entries(cleanData).filter(([k]) => !ASSESSOR_FIELDS.has(k))
    );

    // Auto-determine status: 'finished' if either:
    // 1. Every surface entry has real user-entered measurements (auto-finish), OR
    // 2. The measurer manually marked it complete (manualComplete flag)
    const entries = measurerData.surfaceEntries || [];
    const allMeasured = entries.length > 0 && entries.every(e => isUserMeasured(e.substrate, e));
    const status = (allMeasured || measurerData.manualComplete) ? 'finished' : 'in_progress';

    // If this area is claiming a hero photo, clear it from all other areas first
    const thisAreaHasHero = (measurerData.photos || []).some(p => p.isProjectHero);

    if (currentArea.isNew) {
      const ref = await addDoc(areasCol, stripUndefined({
        ...measurerData, status, createdAt: now, updatedAt: now,
      }));
      if (thisAreaHasHero) await clearHeroFromOtherAreas(pid, ref.id);
      await syncProjectCounts(pid);
      // Track contributor so they can archive/delete this project
      if (userId) await updateDoc(doc(db, 'projects', pid), { contributors: arrayUnion(userId), measuredByNames: arrayUnion(currentUser?.displayName || '') });
      logActivity(pid, 'measured', `${measurerData.name || 'Area'} — ${status === 'finished' ? '✓ finished' : 'saved'}`);
      if (mode === 'stay' || mode === 'finished_stay') {
        setCurrentArea({ firestoreId: ref.id, ...measurerData, status });
      } else if (advanceTo) {
        goToArea(advanceTo);
      } else if (cameFromReview) {
        setCameFromReview(false); setScreen('review'); setCurrentArea(null);
      } else {
        setScreen('project'); setCurrentArea(null);
      }
    } else {
      const areaRef = doc(db, 'projects', pid, 'areas', currentArea.firestoreId);
      await updateDoc(areaRef, stripUndefined({
        ...measurerData, status, updatedAt: now,
      }));
      if (thisAreaHasHero) await clearHeroFromOtherAreas(pid, currentArea.firestoreId);
      await syncProjectCounts(pid);
      // Track contributor so they can archive/delete this project
      if (userId) await updateDoc(doc(db, 'projects', pid), { contributors: arrayUnion(userId), measuredByNames: arrayUnion(currentUser?.displayName || '') });
      logActivity(pid, 'measured', `${measurerData.name || 'Area'} — ${status === 'finished' ? '✓ finished' : 'saved'}`);
      if (mode === 'stay' || mode === 'finished_stay') {
        setCurrentArea({ ...currentArea, ...measurerData, status });
      } else if (advanceTo) {
        goToArea(advanceTo);
      } else if (cameFromReview) {
        setCameFromReview(false); setScreen('review'); setCurrentArea(null);
      } else {
        setScreen('project'); setCurrentArea(null);
      }
    }
  };

  const deleteArea = async (areaFirestoreId) => {
    const pid = currentProject.firestoreId;
    await deleteDoc(doc(db, 'projects', pid, 'areas', areaFirestoreId));
    await syncProjectCounts(pid);
  };

  const duplicateArea = async (area) => {
    const pid = currentProject.firestoreId;
    try {
      const groupIdMap = {};
      const newGroups = (area.repeatGroups || []).map((g, i) => {
        const newId = `rg_${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`;
        groupIdMap[g.id] = newId;
        return { ...g, id: newId };
      });
      const { firestoreId: _fid, editLock: _lock, ...areaData } = area;
      const newArea = {
        ...areaData,
        name: `Copy of ${area.name}`,
        // Clear photos and notes
        photos: [],
        scopeNotes: '',
        scopeExclusions: '',
        defectsNote: '',
        // Clear all assessment data
        assessBySubstrate: {},
        assessHours: null,
        assessNotes: '',
        accessNeeds: [],
        pmAddedSurfaces: [],
        approvedHours: null,
        approvedBySubstrate: {},
        conditionBySubstrate: {},
        // Reset status
        status: 'in_progress',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        surfaceEntries: (area.surfaceEntries || []).map((e, i) => ({
          ...e,
          id: `${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`,
          repeatGroupId: e.repeatGroupId ? (groupIdMap[e.repeatGroupId] || null) : null,
        })),
        repeatGroups: newGroups,
        // Remap itemOrder group references to match new group IDs
        itemOrder: (area.itemOrder || []).map(item =>
          item.type === 'group' && item.groupId && groupIdMap[item.groupId]
            ? { ...item, groupId: groupIdMap[item.groupId] }
            : item
        ),
      };
      await addDoc(collection(db, 'projects', pid, 'areas'), stripUndefined(newArea));
      await syncProjectCounts(pid);
      showAppToast(`"Copy of ${area.name || 'Unnamed'}" created`);
      logActivity(pid, 'duplicated', `Duplicated "${area.name || 'Unnamed'}"`);
    } catch (err) {
      console.error('Duplicate failed:', err);
      setSuccessPrompt({ title: '❌ Duplicate failed', message: `Could not duplicate the area: ${err.message}` });
    }
  };

  // ── Assess Mode ─────────────────────────────────────────────────────────
  const openAssessMode = (targetAreaId) => {
    const pid = currentProject?.firestoreId;
    if (!pid) return;
    const wf = currentProject.workflow || WORKFLOW.MEASURING;
    // Advance workflow when entering assess mode:
    // - READY_TO_ASSESS: normal forward flow — measurer finished, assessor picks up
    // - MEASURING: reverse flow — assessor arrives on site before/during measuring
    if (wf === WORKFLOW.READY_TO_ASSESS || wf === WORKFLOW.MEASURING) {
      setProjectWorkflow(pid, WORKFLOW.ASSESSING);
    }
    setAssessTargetAreaId(targetAreaId || null);
    setScreen('assess');
  };

  const saveAssessArea = async (areaFirestoreId, assessData) => {
    const pid = currentProject?.firestoreId;
    if (!pid || !areaFirestoreId) return;
    const areaRef = doc(db, 'projects', pid, 'areas', areaFirestoreId);
    // Only write non-undefined fields — photos array is optional
    const payload = {
      assessBySubstrate: assessData.assessBySubstrate,
      assessNotes: assessData.assessNotes,
      accessNeeds: assessData.accessNeeds,
      accessNeedsOtherText: assessData.accessNeedsOtherText || '',
      accessOtherLabels: assessData.accessOtherLabels || {},
      pmAddedSurfaces: assessData.pmAddedSurfaces,
      washdownHours: assessData.washdownHours || '',
      conditionBySubstrate: assessData.conditionBySubstrate || {},
      updatedAt: new Date().toISOString(),
    };
    // If PM photos were added, save the complete photos array (existing + new PM photos)
    if (assessData.photos) {
      payload.photos = assessData.photos;
    }
    await updateDoc(areaRef, stripUndefined(payload));
    // Track contributor and sync counts (updates assessedAreaCount on project card)
    if (userId) await updateDoc(doc(db, 'projects', pid), { contributors: arrayUnion(userId), assessedByNames: arrayUnion(currentUser?.displayName || '') });
    await syncProjectCounts(pid);
    // Log — find area name from current project
    const areaName = (areas || []).find(a => a.firestoreId === areaFirestoreId)?.name || 'Area';
    const totalHours = Object.values(assessData.assessBySubstrate || {}).reduce((s, v) => s + (parseFloat(v.hours) || 0), 0);
    logActivity(pid, 'assessed', `${areaName}${totalHours > 0 ? ` — ${totalHours.toFixed(1)}h` : ''}`);
  };

  // PM creates an area that needs measuring — flagged for measurer notification
  const createPMArea = async (areaData) => {
    const pid = currentProject?.firestoreId;
    if (!pid) return;
    const areasCol = collection(db, 'projects', pid, 'areas');
    const now = new Date().toISOString();
    await addDoc(areasCol, stripUndefined({
      name: areaData.name || 'PM Area',
      type: areaData.type || 'internal',
      pmAdded: true,
      pmNote: areaData.pmNote || '',
      pmSurfaces: areaData.pmSurfaces || [],
      pmAddedBy: currentUser?.displayName || 'PM',
      pmAddedAt: now,
      surfaceEntries: [],
      photos: areaData.photos || [],
      status: 'in_progress',
      createdAt: now,
      updatedAt: now,
    }));
    await syncProjectCounts(pid);
    if (userId) await updateDoc(doc(db, 'projects', pid), { contributors: arrayUnion(userId), assessedByNames: arrayUnion(currentUser?.displayName || '') });
    logActivity(pid, 'pm_added_area', `Added missing area: ${areaData.name || 'PM Area'}`);
  };

  const completeAssessment = async (allComplete) => {
    const pid = currentProject?.firestoreId;
    if (!pid) return;
    await setProjectWorkflow(pid, allComplete ? WORKFLOW.ASSESSED : WORKFLOW.ASSESSING, {
      assessedBy: currentUser?.displayName || 'Project Manager',
      assessedAt: new Date().toISOString(),
    });
    setScreen('project');
    if (allComplete) {
      logActivity(pid, 'assessment_complete', 'All areas assessed — BDM notified');
      const projName = currentProject.projectName || currentProject.clientName || 'Project';
      const jobNum = currentProject.jobNumber ? ` (Job #${currentProject.jobNumber})` : '';
      setSuccessPrompt({
        title: '✅ Assessment complete!',
        message: `All areas have been assessed.\n\nNext step: Notify your BDM/Estimator so they can review the results.`,
        emailAction: {
          subject: `Site2Scope: Assessment complete — ${projName}`,
          body: `Hi,\n\nThe assessment for "${projName}"${jobNum} is now complete. All areas have assessed paint hours entered.\n\nPlease open Site2Scope and search for the project to review the assessed hours.\n\nThanks`,
        },
      });
    } else {
      setSuccessPrompt({
        title: '💾 Progress saved',
        message: 'Your assessment progress has been saved. You can return to Assess Mode any time to continue where you left off.',
      });
    }
  };

  // ── Review Mode ─────────────────────────────────────────────────────────
  const [cameFromReview, setCameFromReview] = useState(false);

  const openReviewMode = () => {
    const pid = currentProject?.firestoreId;
    const wf = currentProject?.workflow;
    // Auto-advance to COMPARING only when coming from formal ASSESSED state
    if (pid && wf === WORKFLOW.ASSESSED) {
      setProjectWorkflow(pid, WORKFLOW.COMPARING);
    }
    setScreen('review');
  };

  const reviewAmendArea = (area) => {
    setCameFromReview(true);
    setAreaFormType(area.type);
    setCurrentArea(area);
    setAreaFormKey(k => k + 1);
    setScreen('area');
  };

  const saveApprovedHours = async (approvedMap, markApproved = false) => {
    const pid = currentProject?.firestoreId;
    if (!pid) return;
    // New format: approvedMap = { [areaId]: { approvedBySubstrate: {...}, approvedHours: '12.5' } }
    for (const [areaId, data] of Object.entries(approvedMap)) {
      if (!areaId) continue;
      const areaRef = doc(db, 'projects', pid, 'areas', areaId);
      await updateDoc(areaRef, stripUndefined({
        approvedBySubstrate: data.approvedBySubstrate || {},
        approvedHours: data.approvedHours || null,
        updatedAt: new Date().toISOString(),
      }));
    }
    await syncProjectCounts(pid);
    // Optionally mark project as approved
    if (markApproved) {
      const totalHours = Object.values(approvedMap).reduce((s, d) => s + (parseFloat(d.approvedHours) || 0), 0);
      await setProjectWorkflow(pid, WORKFLOW.APPROVED, {
        approvedAt: new Date().toISOString(),
      });
      logActivity(pid, 'adjusted', `Hours finalised — ${totalHours.toFixed(1)}h total`);
      setScreen('project');
      const projName = currentProject.projectName || currentProject.clientName || 'Project';
      const jobNum = currentProject.jobNumber ? ` (Job #${currentProject.jobNumber})` : '';
      setSuccessPrompt({
        title: '✅ Hours adjusted',
        message: 'Adjusted hours have been saved.\n\nNext step: Export your Scope PDF from the export panel. Select "Adjusted Hours" to generate a document with your final figures.',
        emailAction: {
          subject: `Site2Scope: Hours adjusted — ${projName}`,
          body: `Hi,\n\nThe hours for "${projName}"${jobNum} have been adjusted and finalised.\n\nOpen Site2Scope and search for the project to view the final figures.\n\nThanks`,
        },
      });
    }
  };

  // ── Filter projects by status ──────────────────────────────────────────
  const visibleProjects = projects.filter(p =>
    showArchived ? p.status === 'archived' : (p.status || 'active') === 'active'
  );

  if (loading) return (
    <div className="s2s-screen">
      <div className="s2s-header">
        <div style={{width: 120, height: 40, background: 'rgba(255,255,255,0.1)', borderRadius: 8}}></div>
        <div style={{width: 100, height: 32, background: 'rgba(255,255,255,0.1)', borderRadius: 8}}></div>
      </div>
      <div style={{padding: 16, maxWidth: 800, margin: '0 auto', width: '100%'}}>
        <div className="skeleton skeleton-text" style={{width: '50%', marginBottom: 16}}></div>
        {[1,2,3].map(i => <div key={i} className="skeleton skeleton-card"></div>)}
      </div>
    </div>
  );

  return (
    <div className="s2s-app">
      {successPrompt && (
        <div className="cm-overlay" onClick={() => setSuccessPrompt(null)}>
          <div className="cm-dialog" onClick={e => e.stopPropagation()}>
            <div className="cm-title">{successPrompt.title}</div>
            <div className="cm-message" style={{whiteSpace:'pre-line'}}>{successPrompt.message}</div>
            <div className="cm-buttons">
              {successPrompt.confirmAction && (
                <button className="cm-btn cm-cancel" onClick={() => setSuccessPrompt(null)}>Cancel</button>
              )}
              {successPrompt.emailAction && (
                <a className="cm-btn cm-confirm" style={{background:'#059669',textDecoration:'none',textAlign:'center'}}
                  href={`mailto:?subject=${encodeURIComponent(successPrompt.emailAction.subject)}&body=${encodeURIComponent(successPrompt.emailAction.body)}`}>
                  ✉️ Email
                </a>
              )}
              <button className="cm-btn cm-confirm" onClick={() => {
                if (successPrompt.confirmAction) successPrompt.confirmAction();
                else setSuccessPrompt(null);
              }}>{successPrompt.confirmAction ? 'Open Anyway' : 'Got it'}</button>
            </div>
          </div>
        </div>
      )}
      {appToast && (
        <div className="pd-archive-toast" style={{bottom: 80}}>
          <span>{appToast}</span>
        </div>
      )}
      <ToastContainer />
      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
      {screen === 'projects' && (
        <ErrorBoundary fallbackMessage="The project list encountered an error. Try reloading the app.">
        <ProjectsList
          projects={visibleProjects}
          showArchived={showArchived}
          onToggleArchived={() => setShowArchived(v => !v)}
          onSelect={(p) => { setCurrentProject(p); setScreen('project'); }}
          onDelete={deleteProject}
          onArchive={archiveProject}
          onUpdate={updateProject}
          onUnarchive={unarchiveProject}
          onCreate={createProject}
          currentUser={currentUser}
          onSignOut={signOut}
          onUpdateDisplayName={async (newName) => {
            if (!userId || !newName.trim()) return;
            const trimmed = newName.trim();
            await updateDoc(doc(db, 'users', userId), { displayName: trimmed });
            setAuthDisplayName(trimmed);
          }}
        />
        </ErrorBoundary>
      )}
      {screen === 'project' && projectWithAreas && (
        <ErrorBoundary onReset={() => { setScreen('projects'); setCurrentProject(null); }} fallbackMessage="The project detail screen encountered an error. Click Go Back to return to the project list.">
        <ProjectDetail
          project={projectWithAreas}
          onBack={() => { setScreen('projects'); setCurrentProject(null); }}
          onNewArea={openNewArea}
          onEditArea={openEditArea}
          onDeleteArea={deleteArea}
          onDuplicateArea={duplicateArea}
          onDelete={() => deleteProject(currentProject.firestoreId)}
          onArchive={() => archiveProject(currentProject.firestoreId)}
          onSaveDetails={saveProjectDetails}
          onSetWorkflow={(wf, extra) => setProjectWorkflow(currentProject.firestoreId, wf, extra)}
          onAssess={openAssessMode}
          onReview={openReviewMode}
          onAcceptIncompleteAreas={async (areaIds) => {
            const pid = currentProject?.firestoreId;
            if (!pid) return;
            for (const areaId of areaIds) {
              await updateDoc(doc(db, 'projects', pid, 'areas', areaId), {
                manualComplete: true,
                status: 'finished',
                updatedAt: new Date().toISOString(),
              });
            }
            await syncProjectCounts(pid);
          }}
          currentUser={currentUser}
          onSignOut={signOut}
          onLogActivity={(action, detail) => logActivity(currentProject.firestoreId, action, detail)}
          activeRole={activeRole}
          onSetActiveRole={setActiveRole}
          onUpdateDisplayName={async (newName) => {
            if (!userId || !newName.trim()) return;
            const trimmed = newName.trim();
            await updateDoc(doc(db, 'users', userId), { displayName: trimmed });
            setAuthDisplayName(trimmed);
          }}
        />
        </ErrorBoundary>
      )}
      {screen === 'area' && currentProject && (
        <ErrorBoundary onReset={() => { setScreen('project'); setCurrentArea(null); setCameFromReview(false); }} fallbackMessage="The measurement form encountered an error. Click Go Back to return to the project.">
        <AreaForm
          // Keyed on areaFormKey alone (bumped only when the user explicitly opens a
          // new/different area), NOT on currentArea.firestoreId — a brand-new area's
          // firestoreId is assigned mid-session by the background save in saveArea(),
          // and keying on it there would remount AreaForm (wiping local state like
          // the Save → New Area type-picker) out from under the user.
          key={areaFormKey}
          area={currentArea}
          type={areaFormType}
          projectId={currentProject.firestoreId}
          projectHasHero={areas.some(a => a.firestoreId !== currentArea?.firestoreId && (a.photos || []).some(p => p.isProjectHero))}
          onSave={saveArea}
          onBack={() => {
            if (cameFromReview) { setCameFromReview(false); setScreen('review'); }
            else { setScreen('project'); }
            setCurrentArea(null);
          }}
          onNewArea={(nextType, prevName) => { openNewArea(nextType, prevName); }}
          nextArea={nextAreaInSequence}
          areaPosition={currentAreaIndex >= 0 ? { index: currentAreaIndex + 1, total: orderedAreas.length } : null}
        />
        </ErrorBoundary>
      )}
      {screen === 'assess' && currentProject && projectWithAreas && (
        <ErrorBoundary onReset={() => { setAssessTargetAreaId(null); setScreen('project'); }} fallbackMessage="The assessment screen encountered an error. Click Go Back to return to the project.">
        <AssessForm
          project={projectWithAreas}
          targetAreaId={assessTargetAreaId}
          onSaveArea={saveAssessArea}
          onCreatePMArea={createPMArea}
          onSaveProjectFields={saveProjectDetails}
          onComplete={completeAssessment}
          onBack={() => { setAssessTargetAreaId(null); setScreen('project'); }}
        />
        </ErrorBoundary>
      )}
      {screen === 'review' && currentProject && projectWithAreas && (
        <ErrorBoundary onReset={() => setScreen('project')} fallbackMessage="The review screen encountered an error. Click Go Back to return to the project.">
        <ReviewMode
          project={projectWithAreas}
          onAmendArea={reviewAmendArea}
          onSaveApproved={saveApprovedHours}
          onBack={() => setScreen('project')}
          readOnly={activeRole === 'assessor'}
        />
        </ErrorBoundary>
      )}
      <button className="s2s-help-fab" onClick={() => setShowHelp(true)} title="Help &amp; Guides">?</button>
      {swUpdateWaiting && (
        <div className="s2s-update-banner">
          <span>New version available</span>
          <button className="s2s-update-btn" onClick={() => applyUpdate()}>Update now</button>
        </div>
      )}
    </div>
  );
}

export default App;
